import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  List,
  Edit,
  Create,
  SimpleForm,
  TextField,
  EditButton,
  ReferenceInput,
  SelectInput,
  required,
  ReferenceField,
  SearchInput,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  usePermissions,
  useEditContext,
  useRecordContext,
  Toolbar,
  SaveButton,
  useTranslate,
  useLocaleState,
} from 'react-admin';

import EmptyList from '../components/misc/EmptyList';
import FeaturedVenueButton from '../components/venues/FeaturedVenueButton';
import FeaturedSortedDatagrid from '../components/venues/FeaturedSortedDatagrid';
import VenueListActions from '../components/venues/VenueListActions';
import VenueFormFields from '../components/venues/VenueFormFields';
import ResourceListTabs from '../components/misc/ResourceListTabs';
import { ApplicationDatagrid } from './eventCreationApplications';
import { usePendingApplicationCount } from '../hooks/usePendingApplications';
import {
  VENUE_APPLICATION_FILTERS,
  TABBED_LIST_SX,
  TabbedListActionsPlaceholder,
} from '../components/misc/tabbedListLayout';
import { getFeaturedVenueIds } from '../services/featuredVenuesService';
import { COMPACT_FORM_SX, COMPACT_PAGE_SX } from '../components/form/FormSection';

const entityMultiple = 'venues';

export const VenueList = (props) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { permissions } = usePermissions();
  const [featuredIds, setFeaturedIds] = React.useState([]);
  const [searchParams] = useSearchParams();
  const isApplications = searchParams.get('tab') === 'applications';
  const pendingApplications = usePendingApplicationCount('venue');

  const localeNameKey = locale === 'tr' ? 'name.tr' : 'name.en';

  const filters = [
    <SearchInput source="name" key="search" />,
    <ReferenceInput source="city" reference="cities" key="city">
      <SelectInput optionText={localeNameKey} fullWidth />
    </ReferenceInput>,
    <ReferenceInput source="category" reference="venueCategories" key="category">
      <SelectInput optionText={localeNameKey} fullWidth />
    </ReferenceInput>,
  ];

  React.useEffect(() => {
    let cancelled = false;
    getFeaturedVenueIds()
      .then((ids) => {
        if (!cancelled) setFeaturedIds(ids);
      })
      .catch(() => {
        if (!cancelled) setFeaturedIds([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!permissions) return null;

  let filter = {};
  if (permissions.crole && permissions.crole === 'venueManager' && permissions.rid) {
    filter = { id: permissions.rid };
  }

  if (
    permissions.crole &&
    permissions.crole !== 'admin' &&
    permissions.crole !== 'operator' &&
    !permissions.rid
  )
    return (
      <EmptyList
        customMessage={translate('tickets.no_permission', {
          _: 'Bu sayfayı görüntülemek için yetkiniz bulunmamaktadır.',
        })}
        noCreate={true}
      />
    );

  const canFeature = permissions.crole === 'admin' || permissions.crole === 'operator';
  const canViewApplications = permissions.crole === 'admin' || permissions.crole === 'operator';

  const venueTabs = [
    { id: 'list', label: translate('venues.tab_list', { _: 'Mekanlar' }) },
    {
      id: 'applications',
      label: translate('venues.tab_applications', { _: 'Başvurular' }),
      badge: pendingApplications,
    },
  ];
  const showingApplications = isApplications && canViewApplications;

  return (
    <List
      {...props}
      resource={showingApplications ? 'eventCreationApplications' : 'venues'}
      filter={showingApplications ? { companyType: 'venue', isChecked: false } : filter}
      empty={
        <EmptyList entity={showingApplications ? 'eventCreationApplications' : entityMultiple} />
      }
      filters={showingApplications ? VENUE_APPLICATION_FILTERS : filters}
      title={
        showingApplications
          ? translate('venues.title_applications', { _: 'Mekan Başvuruları' })
          : translate('venues.title', { _: 'Mekanlar' })
      }
      hasCreate={
        !showingApplications &&
        permissions.crole &&
        (permissions.crole === 'admin' || permissions.crole === 'operator')
      }
      actions={showingApplications ? <TabbedListActionsPlaceholder /> : <VenueListActions />}
      exporter={false}
      sort={showingApplications ? { field: 'createdate', order: 'DESC' } : undefined}
      perPage={showingApplications ? 25 : 100}
      sx={TABBED_LIST_SX}
    >
      {canViewApplications ? (
        <ResourceListTabs
          activeTab={showingApplications ? 'applications' : 'list'}
          tabs={venueTabs}
        />
      ) : null}
      {showingApplications ? (
        <ApplicationDatagrid />
      ) : (
        <FeaturedSortedDatagrid featuredIds={featuredIds} bulkActionButtons={false}>
          <TextField
            source="name"
            label={translate('resources.venues.fields.name', { _: 'Mekan Adı' })}
          />
          <ReferenceArrayField
            source="categories"
            reference="venueCategories"
            label={translate('resources.venues.fields.categories', { _: 'Kategoriler' })}
          >
            <SingleFieldList linkType={false}>
              <ChipField source={localeNameKey} />
            </SingleFieldList>
          </ReferenceArrayField>
          <ReferenceArrayField
            source="managers"
            reference="users"
            label={translate('resources.venues.fields.managers', { _: 'Yöneticiler' })}
          >
            <SingleFieldList linkType={false}>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>
          <ReferenceField
            source="city"
            reference="cities"
            label={translate('resources.venues.fields.city', { _: 'Şehir' })}
            link={false}
          >
            <TextField source={localeNameKey} />
          </ReferenceField>
          {canFeature ? (
            <FeaturedVenueButton
              label={translate('venues.feature_button', { _: 'Öne Çıkar' })}
              featuredIds={featuredIds}
              onFeaturedChange={setFeaturedIds}
            />
          ) : null}
          <EditButton basePath={`/${entityMultiple}`} />
        </FeaturedSortedDatagrid>
      )}
    </List>
  );
};

export const VenueCreate = (props) => {
  const translate = useTranslate();

  return (
    <Create
      title={translate('venues.new_venue', { _: 'Yeni Mekan' })}
      {...props}
      sx={COMPACT_PAGE_SX}
    >
      <SimpleForm
        redirect={`/${entityMultiple}`}
        toolbar={<VenueSaveToolbar isEdit={false} />}
        sx={COMPACT_FORM_SX}
      >
        <VenueFormFields isEdit={false} />
      </SimpleForm>
    </Create>
  );
};

export const VenueEdit = (props) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  if (!permissions) return null;

  return (
    <Edit
      title={translate('venues.edit_venue', { _: 'Mekanı Düzenle' })}
      {...props}
      sx={COMPACT_PAGE_SX}
    >
      <SimpleForm
        redirect={`/${entityMultiple}`}
        toolbar={<VenueSaveToolbar isEdit />}
        sx={COMPACT_FORM_SX}
      >
        <EditForm permissions={permissions} />
      </SimpleForm>
    </Edit>
  );
};

const EditForm = ({ permissions }) => {
  const { record, isLoading } = useEditContext();
  if (isLoading) return null;

  return <VenueFormFields isEdit record={record} permissions={permissions} />;
};

const VenueSaveToolbar = ({ isEdit = false, ...props }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const record = useRecordContext();

  return (
    <Toolbar {...props}>
      <SaveButton
        label={translate('ra.action.save', { _: 'Kaydet' })}
        transform={(data) => ({
          ...data,
          name_lc: (data?.name || '').toString().toLowerCase(),
          sourceLang: isEdit ? record?.sourceLanguage || 'tr' : locale || 'tr',
        })}
        type="button"
      />
    </Toolbar>
  );
};
